<template>
  <div>
    <external-product-dialog
      :url="product.detailsSource"
      :showDialog="showDetailsDialog"
      @close="showDetailsDialog = false"
    ></external-product-dialog>
    <v-card
      width="630"
      class="sigfloCard sigfloNeutralBorder150 elevation-0"
      :class="getClass"
      :loading="configurator.productLoading"
    >
      <v-card-text class="d-flex">
        <product-image-carousel :product="product" :allowCarouselDialog="true" />
        <div class="pa-4 productData">
          <div class="d-flex">
            <div class="textDefault sigfloBodyLarge">{{ product.name }}</div>
            <v-spacer />
            <div v-if="!productHasPriceRange(product)" class="textEmphasis sigfloBodyLarge">
              ${{ formatPrice(product.sellPrice) }}
            </div>
            <div v-if="productHasPriceRange(product)" class="textEmphasis sigfloBodyLarge">
              {{ getPriceRange(product) }}
            </div>
          </div>
          <div class="sigfloBodySmall400 textDefault">
            {{ product.sku }}
          </div>
          <div class="textMuted sigfloBody400 longDescription">
            {{ product.longDescription ? product.longDescription : product.description }}
          </div>
          <v-divider class="my-4" />
          <div class="pb-2">
            <v-row>
              <div class="pl-3 pt-2 textEmphasis sigfloBodyMedium">Qty</div>
              <product-quantity-select-button :product="product" class="mr-4" />
              <sig-help-dialog-button
                v-if="product.quantityFormulaDescription"
                logo-side="left"
                title="Calculated Value"
                :message="product.quantityFormulaDescription"
                :show-need-help="false"
              />
              <v-spacer />
              <v-btn
                @click="showDetails"
                v-show="product.detailsSource"
                dark
                rounded
                color="#7C40DC"
                class="sigfloButtonSmall600 elevation-0 text-capitalize mr-4"
                >View Details</v-btn
              >
            </v-row>
          </div>
        </div>
      </v-card-text>

      <v-card-text class="">
        <v-form ref="productForm" submit-prevent>
          <v-row>
            <v-col cols="8">
              <div
                class="accessoryContainer mx-5 my-4"
                v-for="option in product.productSettingModels"
                v-bind:key="option.id"
              >
                <div v-if="option.controlType === 'INT'">
                  <v-text-field
                    solo
                    filled
                    flat
                    dense
                    single-line
                    type="number"
                    persistent-hint
                    :rules="validation.numeric"
                    :suffix="option.valueSuffix"
                    :label="option.name"
                    value=""
                    :hint="option.description"
                  ></v-text-field>
                </div>
                <div v-if="option.controlType === 'DECIMAL'">
                  <v-text-field
                    solo
                    filled
                    flat
                    dense
                    single-line
                    type="number"
                    persistent-hint
                    :rules="validation.numeric"
                    :suffix="option.valueSuffix"
                    :label="option.name"
                    value=""
                    :hint="option.description"
                  ></v-text-field>
                </div>
                <div v-if="option.controlType === 'DropDownList' || option.controlType.startsWith('Multi')">
                  <v-select
                    solo
                    filled
                    flat
                    dense
                    single-line
                    v-model="option.value"
                    persistent-hint
                    :hint="option.description"
                    :items="option.productSettingValueModels"
                    item-text="label"
                    item-value="value"
                    :label="option.name"
                    @change="updateProduct"
                    :rules="validation.selection"
                    :class="getSettingBorderClass(option)"
                  >
                    <template v-slot:append-outer>
                      <v-icon :color="getSettingIconColor(option)">{{ getSettingIcon(option) }}</v-icon>
                    </template>
                  </v-select>
                </div>
                <div v-if="option.controlType.toLowerCase() === 'yesno'">
                  <v-checkbox v-model="option.value" @change="updateProduct"></v-checkbox>
                </div>
              </div>
            </v-col>
            <v-col cols="4" class="pt-6">
              <sig-help-menu-button
                v-if="product.productSettingModels.length > 0"
                :sigfloMessages="settingSigfloMessages"
                logo-side="right"
              />
            </v-col>
          </v-row>
        </v-form>
        <!-- <div class="productQuantityDescription pt-3 ml-2 d-flex" v-if="product.quantityDescription">
          <div>
            <v-img src="@/assets/images/sigfloSymbolLogo.svg" width="50" height="50"></v-img>
          </div>
          <div class="px-1 pt-3 textMutedLight sigfloBodySmall">
            {{ product.quantityDescription }}
          </div>
        </div> -->
      </v-card-text>
      <v-expansion-panels flat v-model="descriptionExpansionPanel">
        <v-expansion-panel>
          <v-expansion-panel-header class="sigfloBody600">{{ product.description }}</v-expansion-panel-header>
          <v-expansion-panel-content class="sigfloBodySmall"
            ><div v-html="product.specifications"></div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <div class="text-right mb-4">
        <v-btn
          @click="unselectProduct"
          rounded
          color="#F4F2F7"
          class="sigfloButtonSmall600 textEmphasis elevation-0 text-capitalize mr-5"
          ><v-icon class="mr-2">mdi-arrow-left</v-icon>Change selection</v-btn
        >
      </div>
    </v-card>
  </div>
</template>

<script>
  import { mapState } from 'vuex';
  import productQuantitySelectButton from './ProductQuantitySelectButton.vue';
  import sigHelpDialogButton from '@/components/shared/SigHelpDialogButton';
  import externalProductDialog from '../../shared/ExternalProductDialog.vue';
  import sigHelpMenuButton from '@/components/shared/SigHelpMenuButton';

  export default {
    props: {
      product: {}
    },
    data() {
      return {
        showDetailsDialog: false,
        descriptionExpansionPanel: 0
      };
    },
    components: {
      sigHelpMenuButton,
      productQuantitySelectButton,
      productImageCarousel: () => import('./ProductImageCarousel.vue'),
      sigHelpDialogButton,
      externalProductDialog
    },
    methods: {
      /**
       * Returns the setting icon color based on the option value
       *
       * @param {*} option
       */
      getSettingIconColor(option) {
        if (option.value) return '#32ff98';
        else return '#F44336';
      },
      /**
       * Returns the setting icon based on the option value
       *
       * @param {*} option
       */
      getSettingIcon(option) {
        if (option.value) return 'mdi-check-circle';
        else return 'mdi-alert';
      },
      /**
       * Opens the details url of the product in an iframe
       */
      showDetails: function () {
        this.showDetailsDialog = true;
      },
      /**
       * Unselects the currently selected product
       */
      unselectProduct() {
        this.$store.commit('configurator/selectProduct', {
          productId: this.product.id,
          selected: false
        });
        this.$store.commit('configurator/incrementSubsystemKey');
        // save new selected state and refresh
        this.saveSubsystemStepProgress('subsystem');
      },
      /**
       * Updates values for this accessory
       */
      updateProduct: function () {
        const _this = this;
        this.$store
          .dispatch('configurator/configureProduct', {
            product: _this.product
          })
          .then(() => {
            this.$store.dispatch('configurator/getSubsystemSteps', {
              subsystemId: _this.configurator.currentSubsystem.id
            });
          });
      },
      /**
       * Returns setting border color class
       *
       * @param {*} option
       */
      getSettingBorderClass: function (option) {
        if (option.value) return '';
        //else return 'redBorder';
      }
    },
    created() {
      this.$eventBus.$on('component-step-navigation', () => {
        if (this.$refs.productForm) {
          this.$refs.productForm.validate();
        }
      });
      if (this.product.quantity < this.product.minQuantity)
        this.$store.commit('configurator/updateProductQuantity', {
          productId: this.product.id,
          quantity: this.product.minQuantity
        });
      else if (this.product.quantity > this.product.maxQuantity)
        this.$store.commit('configurator/updateProductQuantity', {
          productId: this.product.id,
          quantity: this.product.maxQuantity
        });
      else
        this.$store.commit('configurator/updateProductQuantity', {
          productId: this.product.id,
          quantity: this.product.quantity == 0 ? this.product.minQuantity : this.product.quantity
        });
    },
    computed: {
      ...mapState(['configurator']),
      /**
       * Retrieves the sigflo messages associated with the product's setting models.
       * @returns {Array} An array of sigflo messages, each containing an id, label, and message.
       */
      settingSigfloMessages: function () {
        const messages = [];
        if (this.product.productSettingModels) {
          this.product.productSettingModels.forEach((setting, i) => {
            if (setting.sigMessageModel) {
              const message = {
                id: i,
                label: setting.sigMessageModel.messageTitle,
                message: setting.sigMessageModel.message
              };
              messages.push(message);
            }
          });
        }
        return messages;
      },
      /**
       * Returns dynamic class for this card
       */
      getClass: function () {
        let classString = 'selectedBorder';
        let allSettingsHaveValues = true;
        this.product.productSettingModels.forEach((setting) => {
          if (setting.controlType === 'DropDownList' || setting.controlType.startsWith('Multi')) {
            if (!setting.value) allSettingsHaveValues = false;
          }
        });

        if (!this.product.valid && !allSettingsHaveValues) classString += 'redBorder';
        return classString;
      },
      quantity: {
        set(value) {
          this.$store.commit('configurator/updateProductQuantity', {
            productId: this.product.id,
            quantity: value
          });
        },
        get() {
          return this.product.quantity;
        }
      }
    }
  };
</script>

<style scoped>
  .productData {
    min-width: 400px;
  }
  .longDescription {
    max-width: 350px;
  }
  .redBorder {
    border: 1px solid red !important;
  }
  .greenBorder {
    border: 1px solid #32ff98 !important;
  }
  .selectedBorder {
    border: 3px solid #7c40dc !important;
  }
</style>
